import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  Snackbar,
  useMediaQuery,
  TableSortLabel
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import IronIcon from '@mui/icons-material/Iron';
import VerifiedIcon from '@mui/icons-material/Verified';
import BuildIcon from '@mui/icons-material/Build';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { productionApi } from '../api/production';

interface FinishingDesign {
  design_no: string;
  design_image: string | null;
  total_issued: number;
  press_qty: number;
  qc_qty: number;
  in_repair_qty: number;
  barcoded_qty: number;
  damaged_qty: number;
  last_activity_at: string | null;
  oldest_pending_at: string | null;
  batches: Array<{
    batch_number: string;
    garment_part: string;
    total_issued: number;
    press_qty: number;
    qc_qty: number;
    in_repair_qty: number;
    barcoded_qty: number;
    damaged_qty: number;
  }>;
}

type SortKey = 'design_no' | 'total_issued' | 'press_qty' | 'qc_qty' | 'in_repair_qty' | 'barcoded_qty' | 'completion' | 'aging';
type SortDir = 'asc' | 'desc';

const StatCard = ({ title, value, subtitle, icon, color }: {
  title: string; value: string | number; subtitle?: string;
  icon: React.ReactNode; color: string;
}) => (
  <Paper
    sx={{
      p: { xs: 2, sm: 2.5 },
      borderRadius: 3,
      background: `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, ${alpha(color, 0.02)} 100%)`,
      border: `1px solid ${alpha(color, 0.15)}`,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: alpha(color, 0.12), color: color, flexShrink: 0
      }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: 1.2 }}>
          {title}
        </Typography>
        <Typography variant="h5" fontWeight={700} sx={{ color, lineHeight: 1.2 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  </Paper>
);

export default function FinishingDashboardPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [designs, setDesigns] = useState<FinishingDesign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<SortKey>('aging');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Detail dialog
  const [detailDesign, setDetailDesign] = useState<FinishingDesign | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await productionApi.getFinishingDashboard();
      setDesigns(data);
    } catch (err: any) {
      console.error('Error fetching finishing dashboard:', err);
      setError('Failed to load finishing dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Totals for stat cards
  const totals = useMemo(() => {
    return designs.reduce(
      (acc, d) => ({
        press: acc.press + d.press_qty,
        qc: acc.qc + d.qc_qty,
        repair: acc.repair + d.in_repair_qty,
        barcoded: acc.barcoded + d.barcoded_qty
      }),
      { press: 0, qc: 0, repair: 0, barcoded: 0 }
    );
  }, [designs]);

  // Helpers
  const getCompletion = (d: FinishingDesign) =>
    d.total_issued > 0 ? ((d.barcoded_qty + d.damaged_qty) / d.total_issued) * 100 : 0;

  const getAgingDays = (d: FinishingDesign) => {
    if (!d.oldest_pending_at) return -1;
    return Math.floor((Date.now() - new Date(d.oldest_pending_at).getTime()) / (1000 * 60 * 60 * 24));
  };

  const getStuckStage = (d: FinishingDesign): { label: string; color: string } | null => {
    const stages = [
      { label: 'Press', qty: d.press_qty, color: theme.palette.warning.main },
      { label: 'QC', qty: d.qc_qty, color: theme.palette.info.main },
      { label: 'Repair', qty: d.in_repair_qty, color: theme.palette.error.main }
    ];
    const max = stages.reduce((a, b) => (b.qty > a.qty ? b : a), stages[0]);
    if (max.qty === 0) return null;
    return { label: max.label, color: max.color };
  };

  const getAgingColor = (days: number): string => {
    if (days < 0) return theme.palette.text.secondary;
    if (days <= 3) return theme.palette.success.main;
    if (days <= 7) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  // Filtered & sorted list
  const filteredDesigns = useMemo(() => {
    let result = designs;

    // Search
    if (searchFilter) {
      const q = searchFilter.toLowerCase();
      result = result.filter(d => d.design_no.toLowerCase().includes(q));
    }

    // Stage filter
    if (stageFilter === 'press') result = result.filter(d => d.press_qty > 0);
    else if (stageFilter === 'qc') result = result.filter(d => d.qc_qty > 0);
    else if (stageFilter === 'repair') result = result.filter(d => d.in_repair_qty > 0);
    else if (stageFilter === 'completed') result = result.filter(d => getCompletion(d) >= 100);
    else if (stageFilter === 'pending') result = result.filter(d => d.press_qty > 0 || d.qc_qty > 0 || d.in_repair_qty > 0);

    // Sort
    result = [...result].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'design_no': cmp = a.design_no.localeCompare(b.design_no); break;
        case 'total_issued': cmp = a.total_issued - b.total_issued; break;
        case 'press_qty': cmp = a.press_qty - b.press_qty; break;
        case 'qc_qty': cmp = a.qc_qty - b.qc_qty; break;
        case 'in_repair_qty': cmp = a.in_repair_qty - b.in_repair_qty; break;
        case 'barcoded_qty': cmp = a.barcoded_qty - b.barcoded_qty; break;
        case 'completion': cmp = getCompletion(a) - getCompletion(b); break;
        case 'aging': cmp = getAgingDays(a) - getAgingDays(b); break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [designs, searchFilter, stageFilter, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Finishing Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Design-wise overview of the finishing pipeline — track pendings and bottlenecks
          </Typography>
        </Box>

        {/* Stat Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="Pending Press"
              value={totals.press}
              subtitle={`${designs.filter(d => d.press_qty > 0).length} designs`}
              icon={<IronIcon />}
              color="#ea580c"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="Pending QC"
              value={totals.qc}
              subtitle={`${designs.filter(d => d.qc_qty > 0).length} designs`}
              icon={<VerifiedIcon />}
              color="#0891b2"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="In Repair"
              value={totals.repair}
              subtitle={`${designs.filter(d => d.in_repair_qty > 0).length} designs`}
              icon={<BuildIcon />}
              color="#dc2626"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="Barcoded"
              value={totals.barcoded}
              subtitle={`${designs.filter(d => getCompletion(d) >= 100).length} fully done`}
              icon={<QrCode2Icon />}
              color="#059669"
            />
          </Grid>
        </Grid>

        {/* Filters */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Search Design"
            size="small"
            value={searchFilter}
            onChange={e => setSearchFilter(e.target.value)}
            placeholder="Enter design number..."
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filter by Stage</InputLabel>
            <Select
              value={stageFilter}
              label="Filter by Stage"
              onChange={e => setStageFilter(e.target.value)}
            >
              <MenuItem value="all">All Designs</MenuItem>
              <MenuItem value="pending">Has Pendings</MenuItem>
              <MenuItem value="press">Press Pending</MenuItem>
              <MenuItem value="qc">QC Pending</MenuItem>
              <MenuItem value="repair">In Repair</MenuItem>
              <MenuItem value="completed">Fully Completed</MenuItem>
            </Select>
          </FormControl>
          {(searchFilter || stageFilter !== 'all') && (
            <Button variant="outlined" size="small" onClick={() => { setSearchFilter(''); setStageFilter('all'); }}>
              Clear Filters
            </Button>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {filteredDesigns.length} design{filteredDesigns.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        {/* Loading */}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Main Table */}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel active={sortKey === 'design_no'} direction={sortKey === 'design_no' ? sortDir : 'asc'} onClick={() => handleSort('design_no')}>
                    Design
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel active={sortKey === 'total_issued'} direction={sortKey === 'total_issued' ? sortDir : 'asc'} onClick={() => handleSort('total_issued')}>
                    Issued
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel active={sortKey === 'press_qty'} direction={sortKey === 'press_qty' ? sortDir : 'asc'} onClick={() => handleSort('press_qty')}>
                    Press
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel active={sortKey === 'qc_qty'} direction={sortKey === 'qc_qty' ? sortDir : 'asc'} onClick={() => handleSort('qc_qty')}>
                    QC
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel active={sortKey === 'in_repair_qty'} direction={sortKey === 'in_repair_qty' ? sortDir : 'asc'} onClick={() => handleSort('in_repair_qty')}>
                    Repair
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel active={sortKey === 'barcoded_qty'} direction={sortKey === 'barcoded_qty' ? sortDir : 'asc'} onClick={() => handleSort('barcoded_qty')}>
                    Barcoded
                  </TableSortLabel>
                </TableCell>
                {!isMobile && (
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Damaged</TableCell>
                )}
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}>
                  <TableSortLabel active={sortKey === 'completion'} direction={sortKey === 'completion' ? sortDir : 'asc'} onClick={() => handleSort('completion')}>
                    Completion
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel active={sortKey === 'aging'} direction={sortKey === 'aging' ? sortDir : 'asc'} onClick={() => handleSort('aging')}>
                    Aging
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && filteredDesigns.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No designs found</Typography>
                  </TableCell>
                </TableRow>
              )}
              {filteredDesigns.map(d => {
                const completion = getCompletion(d);
                const agingDays = getAgingDays(d);
                const stuck = getStuckStage(d);

                return (
                  <TableRow
                    key={d.design_no}
                    hover
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}
                    onClick={() => setDetailDesign(d)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          variant="rounded"
                          src={d.design_image || undefined}
                          sx={{ width: 36, height: 36, bgcolor: 'grey.200', fontSize: '0.75rem' }}
                        >
                          {d.design_no?.charAt(0) || 'D'}
                        </Avatar>
                        <Typography variant="body2" fontWeight="bold">{d.design_no}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{d.total_issued}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      {d.press_qty > 0 ? (
                        <Chip
                          label={d.press_qty}
                          size="small"
                          sx={{ bgcolor: alpha('#ea580c', 0.1), color: '#ea580c', fontWeight: 'bold', cursor: 'pointer' }}
                          onClick={e => { e.stopPropagation(); navigate(`/production/press?design=${d.design_no}`); }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.disabled">0</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {d.qc_qty > 0 ? (
                        <Chip
                          label={d.qc_qty}
                          size="small"
                          sx={{ bgcolor: alpha('#0891b2', 0.1), color: '#0891b2', fontWeight: 'bold', cursor: 'pointer' }}
                          onClick={e => { e.stopPropagation(); navigate(`/production/qc?design=${d.design_no}`); }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.disabled">0</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {d.in_repair_qty > 0 ? (
                        <Chip
                          label={d.in_repair_qty}
                          size="small"
                          sx={{ bgcolor: alpha('#dc2626', 0.1), color: '#dc2626', fontWeight: 'bold', cursor: 'pointer' }}
                          onClick={e => { e.stopPropagation(); navigate(`/production/repairs?design=${d.design_no}`); }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.disabled">0</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        {d.barcoded_qty}
                      </Typography>
                    </TableCell>
                    {!isMobile && (
                      <TableCell align="center">
                        <Typography variant="body2" color={d.damaged_qty > 0 ? 'error.main' : 'text.disabled'}>
                          {d.damaged_qty}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(completion, 100)}
                          sx={{
                            flex: 1, height: 6, borderRadius: 3,
                            bgcolor: alpha(completion >= 100 ? '#059669' : '#ea580c', 0.1),
                            '& .MuiLinearProgress-bar': {
                              bgcolor: completion >= 100 ? '#059669' : completion >= 50 ? '#0891b2' : '#ea580c',
                              borderRadius: 3
                            }
                          }}
                        />
                        <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 35 }}>
                          {Math.round(completion)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {agingDays >= 0 ? (
                        <Typography variant="body2" fontWeight="bold" sx={{ color: getAgingColor(agingDays) }}>
                          {agingDays === 0 ? 'Today' : `${agingDays}d`}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {stuck ? (
                        <Chip
                          icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                          label={stuck.label}
                          size="small"
                          sx={{ bgcolor: alpha(stuck.color, 0.1), color: stuck.color, fontWeight: 'bold', '& .MuiChip-icon': { color: stuck.color } }}
                        />
                      ) : (
                        completion >= 100 ? (
                          <Chip label="Done" size="small" color="success" variant="outlined" />
                        ) : (
                          <Typography variant="body2" color="text.disabled">-</Typography>
                        )
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Detail Dialog */}
      <Dialog open={!!detailDesign} onClose={() => setDetailDesign(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {detailDesign?.design_image && (
                <Avatar variant="rounded" src={detailDesign.design_image} sx={{ width: 48, height: 48 }} />
              )}
              <Box>
                <Typography variant="h6" fontWeight="bold">{detailDesign?.design_no}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Issued: {detailDesign?.total_issued} pcs
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setDetailDesign(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailDesign && (
            <>
              {/* Summary chips */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                <Chip label={`Press: ${detailDesign.press_qty}`} sx={{ bgcolor: alpha('#ea580c', 0.1), color: '#ea580c', fontWeight: 'bold' }} />
                <Chip label={`QC: ${detailDesign.qc_qty}`} sx={{ bgcolor: alpha('#0891b2', 0.1), color: '#0891b2', fontWeight: 'bold' }} />
                <Chip label={`Repair: ${detailDesign.in_repair_qty}`} sx={{ bgcolor: alpha('#dc2626', 0.1), color: '#dc2626', fontWeight: 'bold' }} />
                <Chip label={`Barcoded: ${detailDesign.barcoded_qty}`} sx={{ bgcolor: alpha('#059669', 0.1), color: '#059669', fontWeight: 'bold' }} />
                <Chip label={`Damaged: ${detailDesign.damaged_qty}`} variant="outlined" color="error" />
              </Box>

              {/* Batch breakdown table */}
              <Typography variant="subtitle2" gutterBottom>Batch Breakdown</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Batch</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Part</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Issued</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Press</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>QC</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Repair</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Barcoded</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Damaged</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailDesign.batches.map(b => (
                      <TableRow key={b.batch_number} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{b.batch_number}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={b.garment_part || 'ALL'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">{b.total_issued}</TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color={b.press_qty > 0 ? '#ea580c' : 'text.disabled'} fontWeight={b.press_qty > 0 ? 'bold' : 'normal'}>
                            {b.press_qty}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color={b.qc_qty > 0 ? '#0891b2' : 'text.disabled'} fontWeight={b.qc_qty > 0 ? 'bold' : 'normal'}>
                            {b.qc_qty}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color={b.in_repair_qty > 0 ? '#dc2626' : 'text.disabled'} fontWeight={b.in_repair_qty > 0 ? 'bold' : 'normal'}>
                            {b.in_repair_qty}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color={b.barcoded_qty > 0 ? '#059669' : 'text.disabled'} fontWeight={b.barcoded_qty > 0 ? 'bold' : 'normal'}>
                            {b.barcoded_qty}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color={b.damaged_qty > 0 ? 'error.main' : 'text.disabled'}>
                            {b.damaged_qty}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDesign(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Error snackbar */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Box>
  );
}
